import React, { useEffect, useRef } from 'react';

interface StrangeFogProps {
  isLiberated: boolean; // true when stele is liberated (VICTORY / EPILOGUE)
  levelId: number;
  expandToBottom?: boolean;
}

export const StrangeFog: React.FC<StrangeFogProps> = React.memo(({ isLiberated, levelId, expandToBottom = false }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !canvas.parentElement) return;
    const ctx = canvas.getContext('2d', { alpha: true });
    if (!ctx) return;

    let animationFrameId: number;
    let isDestroyed = false;
    let parentH = canvas.parentElement.clientHeight || (window.innerHeight * 0.55);
    let width = (canvas.width = canvas.parentElement.clientWidth || window.innerWidth);
    let height = (canvas.height = expandToBottom ? Math.round(parentH * 1.85) : parentH);

    const isDjurdjura = levelId === 2;

    // Pre-create offscreen sprite canvases for puffs (128x128)
    const fogColors = isDjurdjura
      ? [
          { r: 56, g: 189, b: 248 },  // icy cyan
          { r: 148, g: 163, b: 184 }, // misty slate
          { r: 15, g: 23, b: 42 },    // deep obsidian night
          { r: 30, g: 41, b: 59 },    // dark alpine blue
          { r: 203, g: 213, b: 225 }, // frost white
        ]
      : [
          { r: 16, g: 185, b: 129 }, // emerald green
          { r: 52, g: 211, b: 153 }, // light jade green
          { r: 5, g: 150, b: 105 },  // deep forest green
          { r: 13, g: 148, b: 136 }, // mystical teal green
          { r: 74, g: 222, b: 128 }, // glowing green
        ];

    const SPRITE_SIZE = 128;
    const halfSprite = SPRITE_SIZE / 2;
    const puffSprites: HTMLCanvasElement[] = fogColors.map((color) => {
      const spriteCanvas = document.createElement('canvas');
      spriteCanvas.width = SPRITE_SIZE;
      spriteCanvas.height = SPRITE_SIZE;
      const sCtx = spriteCanvas.getContext('2d');
      if (sCtx) {
        const radGrad = sCtx.createRadialGradient(
          halfSprite, halfSprite, 0,
          halfSprite, halfSprite, halfSprite
        );
        radGrad.addColorStop(0, `rgba(${color.r}, ${color.g}, ${color.b}, 0.7)`);
        radGrad.addColorStop(0.45, `rgba(${color.r}, ${color.g}, ${color.b}, 0.35)`);
        radGrad.addColorStop(1, `rgba(${color.r}, ${color.g}, ${color.b}, 0)`);
        sCtx.fillStyle = radGrad;
        sCtx.beginPath();
        sCtx.arc(halfSprite, halfSprite, halfSprite, 0, Math.PI * 2);
        sCtx.fill();
      }
      return spriteCanvas;
    });

    // Cached offscreen vignette & perimeter mist canvas (updated only on resize)
    let vignetteCanvas = document.createElement('canvas');
    const updateVignetteCache = () => {
      vignetteCanvas.width = width;
      vignetteCanvas.height = height;
      const vCtx = vignetteCanvas.getContext('2d');
      if (!vCtx) return;

      const centerX = width / 2;
      const centerY = height / 2;
      const maxDist = Math.hypot(centerX, centerY);

      // 1. Subtle vignette
      const vignetteGrad = vCtx.createRadialGradient(
        centerX, centerY, Math.min(width, height) * 0.18,
        centerX, centerY, maxDist * 0.95
      );
      if (isDjurdjura) {
        vignetteGrad.addColorStop(0, 'rgba(2, 6, 23, 0)');
        vignetteGrad.addColorStop(0.35, 'rgba(2, 6, 23, 0.04)');
        vignetteGrad.addColorStop(0.65, 'rgba(8, 20, 44, 0.25)');
        vignetteGrad.addColorStop(0.85, 'rgba(2, 6, 23, 0.48)');
        vignetteGrad.addColorStop(1, 'rgba(1, 4, 15, 0.65)');
      } else {
        vignetteGrad.addColorStop(0, 'rgba(6, 78, 59, 0)');
        vignetteGrad.addColorStop(0.4, 'rgba(6, 78, 59, 0.03)');
        vignetteGrad.addColorStop(0.7, 'rgba(5, 150, 105, 0.18)');
        vignetteGrad.addColorStop(0.88, 'rgba(4, 120, 87, 0.36)');
        vignetteGrad.addColorStop(1, 'rgba(2, 44, 34, 0.52)');
      }
      vCtx.fillStyle = vignetteGrad;
      vCtx.fillRect(0, 0, width, height);

      // 2. Soft Edge Banks
      const leftMist = vCtx.createLinearGradient(0, 0, width * 0.24, 0);
      leftMist.addColorStop(0, isDjurdjura ? 'rgba(2, 6, 23, 0.45)' : 'rgba(6, 95, 70, 0.28)');
      leftMist.addColorStop(1, 'rgba(0, 0, 0, 0)');
      vCtx.fillStyle = leftMist;
      vCtx.fillRect(0, 0, width * 0.24, height);

      const rightMist = vCtx.createLinearGradient(width, 0, width * 0.76, 0);
      rightMist.addColorStop(0, isDjurdjura ? 'rgba(2, 6, 23, 0.45)' : 'rgba(6, 95, 70, 0.28)');
      rightMist.addColorStop(1, 'rgba(0, 0, 0, 0)');
      vCtx.fillStyle = rightMist;
      vCtx.fillRect(width * 0.76, 0, width * 0.24, height);

      const topMist = vCtx.createLinearGradient(0, 0, 0, height * 0.35);
      if (isDjurdjura) {
        topMist.addColorStop(0, 'rgba(2, 6, 23, 0.68)');
        topMist.addColorStop(0.25, 'rgba(2, 6, 23, 0.52)');
        topMist.addColorStop(0.65, 'rgba(8, 20, 44, 0.25)');
        topMist.addColorStop(1, 'rgba(2, 6, 23, 0)');
      } else {
        topMist.addColorStop(0, 'rgba(2, 44, 34, 0.58)');
        topMist.addColorStop(0.25, 'rgba(2, 44, 34, 0.42)');
        topMist.addColorStop(0.65, 'rgba(4, 120, 87, 0.20)');
        topMist.addColorStop(1, 'rgba(2, 44, 34, 0)');
      }
      vCtx.fillStyle = topMist;
      vCtx.fillRect(0, 0, width, height * 0.35);

      const bottomMist = vCtx.createLinearGradient(0, height, 0, height * 0.75);
      bottomMist.addColorStop(0, isDjurdjura ? 'rgba(2, 6, 23, 0.45)' : 'rgba(6, 78, 59, 0.30)');
      bottomMist.addColorStop(1, 'rgba(0, 0, 0, 0)');
      vCtx.fillStyle = bottomMist;
      vCtx.fillRect(0, height * 0.75, width, height * 0.25);
    };

    updateVignetteCache();

    let resizeTimer: number | null = null;
    const handleResize = () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        if (canvas && canvas.parentElement && !isDestroyed) {
          parentH = canvas.parentElement.clientHeight || (window.innerHeight * 0.55);
          width = canvas.width = canvas.parentElement.clientWidth || window.innerWidth;
          height = canvas.height = expandToBottom ? Math.round(parentH * 1.85) : parentH;
          updateVignetteCache();
        }
      }, 100);
    };
    window.addEventListener('resize', handleResize, { passive: true });

    // Balanced puff count for fluid performance
    const puffCount = expandToBottom ? 22 : 16;
    const puffs = Array.from({ length: puffCount }).map((_, i) => {
      const onSide = Math.random() < 0.7;
      const x = onSide
        ? Math.random() < 0.5
          ? Math.random() * width * 0.25 - 20
          : width - Math.random() * width * 0.25 + 20
        : Math.random() * width;
      const y = Math.random() * height;

      return {
        x,
        y,
        radius: Math.random() * 75 + 65,
        baseAlpha: Math.random() * 0.12 + 0.08,
        vx: (Math.random() * 0.35 + 0.12) * (i % 2 === 0 ? 1 : -1),
        vy: (Math.random() - 0.5) * 0.12,
        pulsing: Math.random() * Math.PI * 2,
        pulseSpeed: Math.random() * 0.015 + 0.006,
        spriteIdx: i % puffSprites.length,
      };
    });

    let currentGlobalFogOpacity = isLiberated ? 0 : 1;
    let targetGlobalFogOpacity = isLiberated ? 0 : 1;
    let dissipationProgress = isLiberated ? 1 : 0;

    const render = () => {
      if (isDestroyed) return;

      // Skip render if document is hidden to conserve power & GPU
      if (document.hidden) {
        animationFrameId = requestAnimationFrame(render);
        return;
      }

      ctx.clearRect(0, 0, width, height);

      targetGlobalFogOpacity = isLiberated ? 0 : 1;
      currentGlobalFogOpacity += (targetGlobalFogOpacity - currentGlobalFogOpacity) * 0.035;

      if (isLiberated) {
        dissipationProgress = Math.min(1, dissipationProgress + 0.02);
      } else {
        dissipationProgress = Math.max(0, dissipationProgress - 0.05);
      }

      // If fog is completely gone and dissipation finished, sleep loop
      if (isLiberated && currentGlobalFogOpacity < 0.005 && dissipationProgress >= 1) {
        return;
      }

      if (currentGlobalFogOpacity > 0.01) {
        const centerX = width / 2;
        const centerY = height / 2;
        const maxDist = Math.hypot(centerX, centerY);

        // 1 & 2. Draw pre-rendered vignette & edge mist in a single blit!
        ctx.globalAlpha = currentGlobalFogOpacity;
        ctx.drawImage(vignetteCanvas, 0, 0);

        // 3. Render swirling fog puffs using fast GPU-cached sprites
        puffs.forEach((puff) => {
          puff.pulsing += puff.pulseSpeed;
          puff.x += puff.vx;
          puff.y += puff.vy;

          if (isLiberated) {
            puff.radius += 1.2;
            puff.x += puff.x > width / 2 ? 1.8 : -1.8;
            puff.y -= 1.0;
          }

          if (puff.x - puff.radius > width + 60) puff.x = -puff.radius - 20;
          if (puff.x + puff.radius < -60) puff.x = width + puff.radius + 20;
          if (puff.y - puff.radius > height + 60) puff.y = -puff.radius - 20;
          if (puff.y + puff.radius < -60) puff.y = height + puff.radius + 20;

          const distToCenter = Math.hypot(puff.x - centerX, puff.y - centerY);
          const centerFalloff = Math.min(1, Math.max(0, (distToCenter - width * 0.18) / (maxDist * 0.55)));

          const dynamicAlpha =
            (puff.baseAlpha + Math.sin(puff.pulsing) * 0.035) *
            currentGlobalFogOpacity *
            centerFalloff;

          if (dynamicAlpha > 0.005) {
            ctx.globalAlpha = dynamicAlpha;
            const sprite = puffSprites[puff.spriteIdx];
            const diameter = puff.radius * 2;
            ctx.drawImage(sprite, puff.x - puff.radius, puff.y - puff.radius, diameter, diameter);
          }
        });
      }

      // 4. Sunbeams / Light Breakthrough when liberated
      if (isLiberated && dissipationProgress > 0 && dissipationProgress < 1) {
        const beamAlpha = Math.sin(dissipationProgress * Math.PI) * 0.45;
        ctx.fillStyle = `rgba(254, 240, 138, ${beamAlpha * 0.35})`;
        ctx.fillRect(0, 0, width, height);
      }

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      isDestroyed = true;
      if (resizeTimer) clearTimeout(resizeTimer);
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
    };
  }, [isLiberated, levelId, expandToBottom]);

  return (
    <div className={`absolute inset-0 pointer-events-none z-25 ${expandToBottom ? 'h-[185%] overflow-visible' : 'h-full overflow-hidden'}`}>
      <canvas ref={canvasRef} className="w-full h-full block" />
    </div>
  );
});
